import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { AnalysisDisplayOption, AnalysisOption, Pipeline } from '../../types/domain';
import { SelectOption } from '../../components/common/Dropdown';
import { getPipeline, getPipelineAnalysisOptions, updatePipeline } from '../../api/pipelineMaintenance/api';
import { showSuccessToast, showErrorToast } from '../../hooks/use-toast';
import { Label } from '../../components/common/FormLabel';
import { ErrorLabel } from '../../components/common/ErrorLabel';
import { Input } from '../../components/common/Input';
import { TextareaField } from '../../components/common/FormFields';
import { FormSingleColumn } from '../../components/specific/pipeline/FormSingleColumn';
import { FormHeader } from '../../components/specific/pipeline/FormHeader';
import { PipelineAnalysisOptions } from '../../components/specific/pipeline/PipelineAnalysisOptions';
import { PipelineStagesTable } from '../../components/specific/pipeline/PipelineStagesTable';
import { SubmitButton } from '../../components/common/Button/SubmitButton';
import { useNavigate, useParams } from 'react-router';
import { ButtonCancel } from '../../components/common/Button';
import { LoadingPage } from '../../components/common/Loading/LoadingPage';
import { LoadingOverlay } from '../../components/common/Loading/LoadingOverlay';
import { useLayout } from '../../contexts/LayoutContext';
import { validateNoHtml } from '../../utils/fieldValidation';

const PipelineMaintenanceEditPipelinePage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: layoutData } = useLayout();
  
  const formMethods = useForm<Pipeline>();
  
  const [analysisOptions, setAnalysisOptions] = useState<SelectOption[]>([]); // available options for dropdown
  const [analysisDisplayOptions, setAnalysisDisplayOptions] = useState<AnalysisDisplayOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pipeline, setPipeline] = useState<Pipeline | undefined>(undefined);
  const erpConfigured = layoutData.erpConfigured;

  useEffect(() => {
    const fetchPipelineData = async () => {
      if (id) {
        const pipeline = await getPipeline(id);
        setPipeline(pipeline);
        
        // Set form values
        if (pipeline.id !== undefined) {
          formMethods.setValue('id', pipeline.id);
        }
        formMethods.setValue('name', pipeline.name);
        formMethods.setValue('description', pipeline.description || '');
        formMethods.setValue('stages', pipeline.stages || []);
        formMethods.setValue('quotationEnabled', pipeline.quotationEnabled);
        
        if (pipeline.analysisDisplayOptions) {
          formMethods.setValue('analysisDisplayOptions', pipeline.analysisDisplayOptions);
        }
      }
    };

    const fetchPipelineAnalysisOptions = async () => {
      const options = await getPipelineAnalysisOptions();
      setAnalysisOptions(options.map((option: AnalysisOption): SelectOption => {
        return {
          value: option.index,
          label: option.fullName
        };
      }));

      setAnalysisDisplayOptions(options.map((option: AnalysisOption): AnalysisDisplayOption => {
        return {
          index: option.index,
          isMandatory: false,
          isReadonly: false,
          shortName: option.shortName,
          fullName: option.fullName
        };
      }));
    };

    const initializePageData = async () => {
      try {
        //need to wait until all data is loaded before we show the page
        await Promise.all([
          fetchPipelineData(),
          fetchPipelineAnalysisOptions()
        ]);
      } catch {
      } finally {
        setLoading(false);
      }
    };

    initializePageData();
  }, [id]);

  /**
   * Handles the form submission.
   * @param {Pipeline} data - The form data.
   */
  const onSubmit: SubmitHandler<Pipeline> = async (data) => {
    setSubmitting(true);
    try {
      await updatePipeline(data);
      showSuccessToast("Pipeline updated successfully.");
      navigate('/PipelineMaintenance');
    } catch {
      showErrorToast("Failed to update pipeline. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingPage message="Loading pipeline..." />;
  }

  return (
    <>
      <LoadingOverlay 
        isVisible={submitting} 
        message="Updating pipeline..." 
      />
      <FormProvider {...formMethods}>
        <FormSingleColumn onSubmit={formMethods.handleSubmit(onSubmit)}>
          <FormHeader title="Pipeline" formInformation="Edit an existing pipeline. This is where you can describe your sales process and configure pipeline analysis options.">
            <SubmitButton text={'Update Pipeline'} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold"/>
            <ButtonCancel text='Cancel' className='text-kPurple-500' />
          </FormHeader>
          <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-5">
              <Label forField='name' label={Translation.PipelineMaintenance.lblName} />
              <ErrorLabel error={formMethods.formState.errors.name?.message} />
              <Input
                inErrorState={!!formMethods.formState.errors.name}
                placeholder="Enter a name for your pipeline"
                {...formMethods.register('name', { 
                  required: 'required', 
                  maxLength: { value: 50, message: 'max 50 characters' },
                  validate: validateNoHtml
                })}
              />
            </div>
            <div className="md:col-span-5">
              <TextareaField
                label="Description"
                labelFor='description'
                error={formMethods.formState.errors.description?.message}
                inErrorState={!!formMethods.formState.errors.description}
                placeholder="Enter a description for your pipeline"
                {...formMethods.register('description', { required: 'required', maxLength: { value: 200, message: 'max 200 characters' }, validate: validateNoHtml })}
              />
            </div>

            {erpConfigured &&
            <div className="md:col-span-5">
              <div className="text-gray-600">
                <p className="font-semibold text-lg py-2">ERP Service Options</p>
                <p>Configure this pipeline to enable ERP service integration.</p>
                <p>Once enabled, pipeline stages can be configured to define which pipeline stage is designated as the quotation stage, and which pipeline stage is designated as the won stage.</p>
                <hr className="pb-4 mt-2" />
              </div>
            </div>}

            {erpConfigured &&
            <div className="md:col-span-5">
              <Label forField='quotationEnabled' label="Quotations Enabled" />
              <br className="pb-4" />
              <Label forField='quotationEnabled' label={pipeline?.quotationEnabled ? "Yes" : "No"} />
              <Input
                type="text" hidden={true}
                {...formMethods.register('quotationEnabled')}
              />
            </div>}

            <div className="md:col-span-5">
              <div className="text-gray-600">
                <p className="font-semibold text-lg py-2">Analysis Display Options</p>
                <p>Which Analysis fields would you like to make available to your pipeline and how should they be configured?</p>
                <p>Selecting 'ReadOnly' or 'Mandatory' is optional.</p>
                <hr className="pb-4 mt-2" />
              </div>
            </div>

            <PipelineAnalysisOptions
              analysisOptions={analysisOptions}
              analysisDisplayOptions={analysisDisplayOptions}
              setValue={formMethods.setValue}
              getValues={formMethods.getValues}
            />

            <PipelineStagesTable quotationEnabled={pipeline?.quotationEnabled || false} />

            <div className="md:col-span-5 text-right mt-4">
              <div className="inline-flex gap-2 w-1/2 flex-row-reverse">
                <SubmitButton text={'Update Pipeline'} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
                <ButtonCancel text='Cancel' className='text-kPurple-500'/>
              </div>
            </div>
          </div>
        </FormSingleColumn>
      </FormProvider>
    </>
  );
};

export default PipelineMaintenanceEditPipelinePage;
